/**
 * CurrencyAmountR — money amount with currency code selector.
 * Handles type: currency_amount.
 * Renders a number input alongside a currency select.
 */

import { useFormContext } from 'react-hook-form';
import type { FieldDef, RendererProps } from '../types';
import { resolveLabel, resolveHelpText, deriveFieldName } from '../utils';
import { buildValidationRules } from '../validation/fromSchema';

// Common currencies — the full list is overkill for immigration forms
const COMMON_CURRENCIES = [
  { value: 'AUD', label: 'AUD — Australian Dollar' },
  { value: 'USD', label: 'USD — US Dollar' },
  { value: 'GBP', label: 'GBP — British Pound' },
  { value: 'EUR', label: 'EUR — Euro' },
  { value: 'NZD', label: 'NZD — New Zealand Dollar' },
  { value: 'SGD', label: 'SGD — Singapore Dollar' },
  { value: 'HKD', label: 'HKD — Hong Kong Dollar' },
  { value: 'CNY', label: 'CNY — Chinese Yuan' },
  { value: 'INR', label: 'INR — Indian Rupee' },
  { value: 'PHP', label: 'PHP — Philippine Peso' },
  { value: 'VND', label: 'VND — Vietnamese Dong' },
  { value: 'THB', label: 'THB — Thai Baht' },
  { value: 'MYR', label: 'MYR — Malaysian Ringgit' },
  { value: 'IDR', label: 'IDR — Indonesian Rupiah' },
  { value: 'CAD', label: 'CAD — Canadian Dollar' },
];

interface Props extends RendererProps {
  field: FieldDef;
}

export function CurrencyAmountR({ field, formId, pdfMode = false, namePrefix, audience }: Props) {
  const { register, getValues } = useFormContext();
  const baseName = deriveFieldName(field, namePrefix);
  const label = resolveLabel(field, formId, audience);
  const hint = resolveHelpText(field, audience);

  const amountName = `${baseName}.amount`;
  const currencyName = `${baseName}.currency`;

  if (pdfMode) {
    const v = getValues(baseName) as { amount?: string | number; currency?: string } | undefined ?? {};
    const display = v.amount != null ? `${v.currency ?? ''} ${Number(v.amount).toLocaleString('en-AU', { minimumFractionDigits: 2 })}`.trim() : null;
    return (
      <div>
        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{label}</span>
        <p className="text-sm text-slate-800 mt-0.5">
          {display ?? <span className="text-slate-400 italic">Not provided</span>}
        </p>
      </div>
    );
  }

  return (
    <div>
      <label className="form-label">
        {label} {field.required && <span className="text-[#FB8E28]">*</span>}
      </label>
      <div className="flex gap-2">
        <select
          {...register(currencyName)}
          className="form-select w-40 shrink-0"
          defaultValue="AUD"
        >
          {COMMON_CURRENCIES.map((c) => (
            <option key={c.value} value={c.value}>{c.value}</option>
          ))}
        </select>
        <input
          {...register(amountName, {
            // RC-4: a required currency field showed an asterisk but registered
            // no `required` rule, so it produced no inline message at all (worse
            // than the generic one). buildValidationRules supplies the same
            // default-label message the submit-gate summary uses; `field.required`
            // gates it, so optional amounts are unaffected. The min rule below
            // stays and wins on its own key.
            required: buildValidationRules(field).required ?? false,
            min: { value: 0, message: 'Amount must be 0 or greater' },
          })}
          type="number"
          step="0.01"
          min="0"
          placeholder="0.00"
          className="form-input flex-1"
        />
      </div>
      {hint && <p className="form-hint">{hint}</p>}
    </div>
  );
}
